import { useEffect } from 'react';
import {
  takePendingRequest,
  createCsrfMiddlewareBundle,
  maybeDownloadAttachment,
  publishResumedResult,
} from '@sentinel/api-client';

/**
 * After returning from a federated step-up redirect, replay the single
 * persisted request once so the user's original action completes.
 *
 * CSRF correctness: the persisted request carries the CSRF header from before
 * the redirect. The step-up elevation rotated the CSRF token. We apply a fresh
 * `createCsrfMiddlewareBundle().applyToRequest()` which (a) re-discovers the
 * current cookie/header names via `/auth/csrf` if needed, (b) reads the
 * current cookie value, and (c) deletes any stale header before setting the
 * current one. The persisted headers are copied first, then the bundle's
 * `applyToRequest` overwrites the CSRF header — stale value never reaches the
 * server.
 *
 * Best-effort, single-use (sessionStorage is cleared by `takePendingRequest`).
 */
export function useResumeFederatedStepUp(onResumed?: () => void): void {
  useEffect(() => {
    const pending = takePendingRequest();
    if (!pending) return;

    void (async () => {
      // Reconstruct the request with persisted headers.
      const headers = new Headers();
      for (const [k, v] of pending.headers) {
        headers.set(k, v);
      }

      const request = new Request(pending.url, {
        method: pending.method,
        headers,
        body: pending.body ?? null,
        credentials: 'same-origin',
      });

      // Apply the current CSRF token. `applyToRequest` discovers the live
      // cookie/header names and overwrites any stale CSRF header already set
      // from the persisted headers above.
      const { applyToRequest } = createCsrfMiddlewareBundle();
      await applyToRequest(request);

      const response = await fetch(request).catch(() => undefined);

      // Attachment (e.g. the account data export ZIP): the body IS the
      // deliverable — trigger the browser download here since the original
      // caller already abandoned the request on the 403.
      const downloaded = response?.ok
        ? await maybeDownloadAttachment(response).catch(() => false)
        : false;

      // Otherwise surface a JSON body (e.g. the one-time API-key secret) back
      // to the originating feature via the in-memory channel. The plaintext
      // secret is never persisted — it lives only in this channel until the
      // page consumes it (or the channel's TTL clears it). A failed replay with
      // a JSON error body is published too, with `ok: false`, so the page can
      // tell "the replay failed" (for example the MCP step-up ticket expired
      // while the user was at the IdP) from "nothing to resume"; every consumer
      // checks `ok` first.
      if (response && !downloaded) {
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
          const body = await response.clone().json().catch(() => undefined);
          publishResumedResult({
            url: pending.url,
            method: pending.method,
            status: response.status,
            ok: response.ok,
            body,
          });
        }
      }

      window.dispatchEvent(new CustomEvent('sentinel:step-up-resumed'));
      onResumed?.();
    })();
    // onResumed is intentionally excluded: the effect is a one-shot on mount.
    // Changing the callback after mount should not re-replay the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
